import { Form } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import { container } from 'src/inversify.config';
import { Alert, AutoComplete, Checkbox, Col, Input, Modal, Row, Select, Spin } from 'antd';
import { nameof } from 'src/core/utils/object';
import NewItemModal from 'src/core/ui/newitemmodal';
import {
  CoreDiameter,
  CreateLicenseDataItem,
  DeviceType,
  LicenseCreationAttributes,
  LicenseCreationAttributesStore,
  LicenseDataItem,
  LicenseDataItemStore,
  LicenseDataSummaryItem,
  LicenseDataSummaryStore,
  LicenseSuggestionsRequest,
  LicenseSuggestionsStore,
  abslouteMaxLicenseDate,
  deviceTypeLabels,
  deviceTypeSorter,
  getLicenseToolCode,
  getSmartCoreLicenseToolDiameter,
  getUnlimitedLicenseAttributes,
  hoursToDateDiff,
  isHourBasedLicense,
} from '../../stores/licensing-store';
import { Query } from '../../core/stores/data-store';
import moment from 'moment';
import { getCurrentOrg, getDateFormat } from '../../common/utils';
import { useIdentity } from '../../core/services/authentication';
import DynamicDatePicker from 'src/common/components/dynamic-date-picker';
import { UserSummaryStore } from 'src/stores/users-store';

const { TextArea } = Input;

const NewLicenseDialog: FC<
  {
    onClose: (response?: CommandResult<LicenseDataItem>) => void;
    attributes: LicenseCreationAttributes;
    isGlobalLicenseManager: boolean;
  } & WithTranslation
> = ({ t, onClose, attributes, isGlobalLicenseManager }) => {
  const identity = useIdentity();
  const [form] = Form.useForm();
  const inputRef = useRef();
  const [customError, setCustomError] = useState('');
  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>();
  const currentOrg = getCurrentOrg(identity);
  const newLicenseStore = container.get(LicenseDataItemStore);
  const newLicenseState = newLicenseStore.state;
  const licenseDataStore = container.get(LicenseDataSummaryStore);
  const licenseSuggestionsStore = container.get(LicenseSuggestionsStore);
  const licenseSuggestionsState = licenseSuggestionsStore.state;
  const [saveLicenseDataWarning, setSaveLicenseDataWarning] = useState(false);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [customerOptions, setCustomerOptions] = useState<string[]>([]);
  const [canCreateUnlimitedLicense, setCanCreateUnlimitedLicense] = useState<boolean>(false);

  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>();
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const existingLicenseRef = useRef<LicenseDataSummaryItem & { currentFrom: Date; currentUntil: Date }>();
  const usersStore = container.get(UserSummaryStore);

  const load = async () => {
    var request = { organizationId: currentOrg.id } as LicenseSuggestionsRequest;
    await licenseSuggestionsStore.getSuggestions(request);
  };

  useEffect(() => {
    load();
  }, [inputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStartDate = (dateInput: moment.Moment) => {
    const date = moment(dateInput.toDate());
    date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    date.add(dateInput.utcOffset(), 'minutes');
    return moment.utc(date);
  };
  const getEndDate = (dateInput: moment.Moment) => {
    const date = moment(dateInput.toDate());
    date.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
    date.add(dateInput.utcOffset(), 'minutes');
    return moment.utc(date);
  };

  const getDeviceOptions = () => {
    let deviceList: DeviceType[] = [];
    if (attributes) {
      deviceList = attributes.allowedDevices;
    } else if (isGlobalLicenseManager) {
      deviceList = Object.values(DeviceType);
    }

    return deviceList.toSorted(deviceTypeSorter).map((y) => {
      return { label: t(deviceTypeLabels[y]), value: y };
    });
  };

  const validateLicense = async () => {
    let item = await getCreateLicenseDataItem();

    let fromDate: Date;
    let untilDate: Date;

    if (!isHourBasedLicense(item.deviceType)) {
      fromDate = item.fromDate?.toDate();
      untilDate = item.untilDate?.toDate();
    } else {
      fromDate = hoursToDateDiff(item.workingHours).from;
      untilDate = hoursToDateDiff(item.workingHours).until;
    }

    const from = moment(fromDate).toISOString();
    const until = moment(untilDate).toISOString();

    const filter = `(FromDate ge ${from} and FromDate le ${until}) or (UntilDate ge ${from} and UntilDate le ${until}) or (FromDate le ${from} and UntilDate ge ${until})`;
    if (validateLicenseAttributes(item)) {
      let toolCodeFilter: string | { and: string[] };
      if (item.deviceType !== DeviceType.SCTD || item.toolCode[0] !== '0') {
        toolCodeFilter = item.toolCode;
      } else {
        toolCodeFilter = {
          and: [`substring(concat('00000', ToolCode), length(concat('00000', ToolCode)) sub 5) eq '${item.toolCode}'`],
        };
      }

      var query = {
        filter: {
          OrganizationId: { and: `OrganizationId eq ${currentOrg.id}` },
          ToolCode: toolCodeFilter,
          DeviceType: item.deviceType,
          DateFilter: item.unlimited ? undefined : { and: [filter] },
          Unlimited: item.unlimited ?? false,
          Coefficients: item.coefficients,
          Continuous: item.continuous,
          Multishot: item.multishot,
          Zupt: item.zupt,
          Offline: item.offline,
          Flash: item.flash,
        },
        skip: 0,
        take: 1,
      } as Query;
      setCustomError('');

      var result = await licenseDataStore.licenseExists(query);
      if (result) {
        existingLicenseRef.current = { ...result, currentFrom: item.fromDate?.toDate(), currentUntil: item.untilDate?.toDate() };
        setSaveLicenseDataWarning(true);
        return;
      } else {
        onNewItem(item);
      }
    }
  };
  const validateLicenseAttributes = (item: any) => {
    if (isGlobalLicenseManager) return true;
    if (attributes) {
      if (!attributes.allowedDevices.some((x) => x === item.deviceType)) {
        setCustomError(t('DeviceTypeNotAllowed'));
        return false;
      } else if (!item.unlimited && moment(attributes.maximumLicenseDueDate).isBefore(item.untilDate, 'day')) {
        setCustomError(t('MaximumLicenseDueDateReached').replace('{0}', moment(attributes.maximumLicenseDueDate).format(getDateFormat())));
        return false;
      } else if (
        !isHourBasedLicense(item.deviceType) &&
        !item.unlimited &&
        item.untilDate.diff(item.fromDate, 'days') > attributes.maxLicensesPeriodInDays
      ) {
        setCustomError(t('MaxLicensesPeriodInDaysReached').replace('{0}', attributes.maxLicensesPeriodInDays.toString()));
        return false;
      } else if (attributes.maxNumberOfLicensesInCurrentMonth <= 0) {
        setCustomError(t('MaxNumberOfLicensesPerMonthReached'));
        return false;
      }
    } else return false;

    return true;
  };
  const getCreateLicenseDataItem = async () => {
    let item: any;
    try {
      item = (await form.validateFields()) as CreateLicenseDataItem;
    } catch {
      setCustomError(t('PleaseCheckTheInfoProvided'));
      return;
    }
    if (!item.unlimited) {
      if (!isHourBasedLicense(item.deviceType)) {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setCustomError(t('PleaseSelectDates'));
          return;
        }
        item.fromDate = getStartDate(dateRange![0]);
        item.untilDate = getEndDate(dateRange![1]);
      }
    } else {
      if (isHourBasedLicense(item.deviceType)) {
        item.workingHours = unlimitedLicenseDefaults.workingHours;
      } else {
        item.fromDate = moment(unlimitedLicenseDefaults.fromDate);
        item.untilDate = moment(unlimitedLicenseDefaults.untilDate);
      }
    }
    item.organizationId = currentOrg.id;
    item.organizationName = currentOrg.name;

    if (item.deviceType === DeviceType.SCTD) {
      item.toolCode = item.toolCode.padStart(4, '0');
      item.toolCode = `${item.coreDiameter}${item.toolCode}`;
    }

    return item;
  };
  const onNewItem = async (item?: CreateLicenseDataItem) => {
    setCustomError('');

    let response = await newLicenseStore.create(item || (await getCreateLicenseDataItem()));
    if (response?.isSuccess) {
      form.resetFields();
      if (item?.deviceType && item?.toolCode && attributes?.needsSignOff) {
        usersStore.sendLicenseCreationEmail({ deviceType: item.deviceType, toolNumber: item.toolCode });
      }
      onClose(response);
    } else setCustomError(t('ThereWasAProblemCreatingLicense'));
    setSaveLicenseDataWarning(false);
  };
  const onProjectSearch = (searchText: string) => {
    setProjectOptions(
      !searchText || !licenseSuggestionsState.value.item.projects
        ? []
        : licenseSuggestionsState.value.item.projects.filter((x) => x.toLowerCase().includes(searchText.toLowerCase()))
    );
  };
  const onCustomerSearch = (searchText: string) => {
    setCustomerOptions(
      !searchText || !licenseSuggestionsState.value.item.customers
        ? []
        : licenseSuggestionsState.value.item.customers.filter((x) => x.toLowerCase().includes(searchText.toLowerCase()))
    );
  };
  const onCanCreateUnlimitedLicenseChange = async (e: any) => {
    let canCreateUnlimited = form.getFieldValue('unlimited');
    setCanCreateUnlimitedLicense(canCreateUnlimited);
  };
  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewLicense')}
      onNewItem={validateLicense}
      onClose={onClose}
      onOkButtonIsDisabled={newLicenseState.isBusy.get()}
      onCancelButtonIsDisabled={newLicenseState.isBusy.get()}
      closable={!newLicenseState.isBusy.get()}
      maskClosable={!newLicenseState.isBusy.get()}
      okText={t('AddLicense')}
      errorMessage={newLicenseState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newLicenseState.isBusy.get() || licenseSuggestionsState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}>
            <Form form={form} layout="vertical" autoComplete="off">
              <Row>
                <Col span={form.getFieldValue('deviceType') === DeviceType.SCTD ? 12 : 24}>
                  <Form.Item name={nameof<CreateLicenseDataItem>('deviceType')} style={{ marginRight: 15 }}>
                    <Select
                      style={{ width: '100%' }}
                      onSelect={(value) => {
                        setSelectedDeviceType(value);
                        if (value === DeviceType.SCTD && form.getFieldValue('toolCode')?.length === 5) {
                          form.setFieldsValue({ toolCode: '' });
                        }
                      }}
                      placeholder={t('DeviceType')}
                      options={getDeviceOptions()}
                    />
                  </Form.Item>
                </Col>

                {form.getFieldValue('deviceType') === DeviceType.SCTD && (
                  <Col span={12}>
                    <Form.Item name={'coreDiameter'} rules={[{ required: true, message: t('PleaseEnterCoreDiameter') }]} style={{ marginRight: 15 }}>
                      <Select
                        style={{ height: '100%' }}
                        placeholder={t('ToolDiameter')}
                        options={Object.entries(CoreDiameter)
                          .filter((v) => isNaN(parseInt(v[0])))
                          .map(([key, value]) => ({ label: key, value }))}
                      />
                    </Form.Item>
                  </Col>
                )}

                <Col span={18}>
                  <Form.Item
                    name={nameof<CreateLicenseDataItem>('toolCode')}
                    rules={[
                      { required: true, message: t('PleaseEnterToolCode') },
                      () => ({
                        validator(_, value: string) {
                          if (parseInt(value) === 0) return Promise.reject(t('InvalidToolcode'));
                          else if (parseInt(value) > 65535) return Promise.reject(t('ToolcodeTooLarge'));
                          return Promise.resolve();
                        },
                      }),
                    ]}
                    style={{ marginRight: 15 }}
                  >
                    <Input
                      maxLength={form.getFieldValue('deviceType') === DeviceType.SCTD ? 4 : 5}
                      autoComplete="off"
                      allowClear
                      placeholder={t('ToolCode')}
                      onChange={(e) => {
                        const regex = /[^0-9]/g;
                        form.setFieldsValue({ toolCode: e.target.value.replace(regex, '') });
                      }}
                    />
                  </Form.Item>
                </Col>

                {(isGlobalLicenseManager || attributes.canCreateUnlimitedLicense) && (
                  <Col span={6}>
                    <Form.Item name={nameof<CreateLicenseDataItem>('unlimited')} valuePropName="checked">
                      <Checkbox onChange={onCanCreateUnlimitedLicenseChange}>{t('Unlimited')}</Checkbox>
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {!canCreateUnlimitedLicense && !isHourBasedLicense(selectedDeviceType) && (
                <Form.Item initialValue={[moment(), null]} rules={[{ required: true, message: t('PleaseEnterStartAndEndDate') }]}>
                  <DynamicDatePicker
                    defaultValue={[moment(), null]}
                    dayRange={attributes ? attributes.maxLicensesPeriodInDays : 0}
                    maxDate={moment(attributes ? attributes.maximumLicenseDueDate : abslouteMaxLicenseDate.toISOString()).startOf('D')}
                    minDate={moment(Date.now()).subtract(2, 'd').startOf('D')}
                    dateRange={dateRange}
                    conditionToDisable={!attributes}
                    style={{ height: 40, width: '100%' }}
                    disabled={canCreateUnlimitedLicense}
                    allowClear={true}
                    onChange={(dateRange: any) => setDateRange(dateRange)}
                    format={getDateFormat()}
                  />
                </Form.Item>
              )}

              {!canCreateUnlimitedLicense && isHourBasedLicense(selectedDeviceType) && (
                <Col span={24}>
                  <Form.Item
                    name={nameof<CreateLicenseDataItem>('workingHours')}
                    rules={[{ required: true, message: t('PleaseEnterWorkingHours') }]}
                    style={{ marginRight: 15 }}
                  >
                    <Input
                      autoComplete="off"
                      allowClear
                      placeholder={t('WorkingHours')}
                      onChange={(e) => {
                        const regex = /[^0-9]/g;
                        form.setFieldsValue({ workingHours: e.target.value.replace(regex, '') });
                      }}
                    />
                  </Form.Item>
                </Col>
              )}

              <Row>
                <Col span={12}>
                  <Form.Item style={{ marginRight: 20 }} name={nameof<CreateLicenseDataItem>('project')} label={t('Project')}>
                    <AutoComplete
                      style={{ width: '100%' }}
                      options={projectOptions.map((x) => {
                        return { value: x };
                      })}
                      onSearch={onProjectSearch}
                      placeholder={t('Project')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={nameof<CreateLicenseDataItem>('customer')} label={t('Customer')}>
                    <AutoComplete
                      style={{ width: '100%' }}
                      options={customerOptions.map((x) => {
                        return { value: x };
                      })}
                      onSearch={onCustomerSearch}
                      placeholder={t('Customer')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name={nameof<CreateLicenseDataItem>('notes')} label={t('Notes')}>
                <TextArea rows={3} placeholder={t('Notes')} autoComplete="off" allowClear />
              </Form.Item>
              {(isGlobalLicenseManager || attributes.canCreateRecurrentLicenses) && (
                <Form.Item name={nameof<CreateLicenseDataItem>('isRecurrent')} valuePropName="checked">
                  <Checkbox>{t('Recurrence')}</Checkbox>
                </Form.Item>
              )}
              {attributes && attributes.maxNumberOfLicensesInCurrentMonth > 0 && (
                <>
                  <Alert
                    message={t('RemainingLicensesMessage').replace('{0}', `${attributes.maxNumberOfLicensesInCurrentMonth}`)}
                    type={attributes.maxNumberOfLicensesInCurrentMonth === 1 ? 'warning' : 'success'}
                  />
                </>
              )}
              {attributes && attributes.maxNumberOfLicensesInCurrentMonth <= 0 && (
                <>
                  <Alert message={t('MaxLicenseNumberReached')} />
                </>
              )}
            </Form>
          </Spin>
          {saveLicenseDataWarning && (
            <Modal
              title={<h3>{`${t('Confirmation')}`}</h3>}
              open={saveLicenseDataWarning}
              onOk={() => onNewItem()}
              onCancel={() => setSaveLicenseDataWarning(false)}
              okText={t('Continue')}
              okButtonProps={{ style: { width: '45%', float: 'right' } }}
              cancelText={t('Cancel')}
              cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
            >
              <p>
                {existingLicenseRef.current?.unlimited
                  ? t(`Unlimited${existingLicenseRef.current.deviceType === DeviceType.SCTD ? 'SmartCore' : ''}LicenseAlreadyExists`)
                      .replace('{0}', `${deviceTypeLabels[existingLicenseRef.current?.deviceType]}`)
                      .replace('{1}', `${getLicenseToolCode(existingLicenseRef.current)}`)
                      .replace('{2}', `${existingLicenseRef.current?.createdBy}`)
                      .replace('{3}', `${moment(existingLicenseRef.current?.createdOn).local().format(getDateFormat())}`)
                      .replace('{4}', `${getSmartCoreLicenseToolDiameter(existingLicenseRef.current)}`)
                  : t(`${existingLicenseRef.current!.deviceType === DeviceType.SCTD ? 'SmartCore' : ''}LicenseAlreadyExists`)
                      .replaceAll('{0}', `${deviceTypeLabels[existingLicenseRef.current!.deviceType]}`)
                      .replaceAll('{1}', `${getLicenseToolCode(existingLicenseRef.current!)}`)
                      .replace('{2}', `${moment(existingLicenseRef.current?.fromDate).utc().format(getDateFormat())}`)
                      .replace('{3}', `${moment(existingLicenseRef.current?.untilDate).utc().format(getDateFormat())}`)
                      .replace('{4}', `${existingLicenseRef.current?.createdBy}`)
                      .replace('{5}', `${moment(existingLicenseRef.current?.createdOn).local().format(getDateFormat())}`)
                      .replace('{6}', `${moment(existingLicenseRef.current?.currentFrom).utc().format(getDateFormat())}`)
                      .replace('{7}', `${moment(existingLicenseRef.current?.currentUntil).utc().format(getDateFormat())}`)
                      .replaceAll('{8}', `${getSmartCoreLicenseToolDiameter(existingLicenseRef.current!)}`)}{' '}
              </p>
            </Modal>
          )}
        </>
      }
    />
  );
};

export default withTranslation()(NewLicenseDialog);
